import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { containersApi, formatError } from "../lib/api";
import { extractQrCode } from "../lib/format";

/**
 * Deep-link landing for the sticker URL form (https://<host>/c/{code}).
 * Anyone arriving here is already authenticated (Gate wraps the route). We
 * just turn around and ask the backend what to do with the code.
 */
const ScanLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Normalize the URL param (React Router already URL-decoded it) —
        // this handles the edge case where someone hits /c/<junk>.
        const cleaned = extractQrCode(code || "");
        if (!cleaned) {
          toast.error("That doesn't look like a QFresh code");
          navigate("/", { replace: true });
          return;
        }
        const result = await containersApi.byQr(cleaned);
        if (cancelled) return;
        if (result.status === "foreign") {
          toast.error("That doesn't look like a QFresh code");
          navigate("/", { replace: true });
          return;
        }
        if (result.status === "not_found") {
          navigate(`/add?qr=${encodeURIComponent(result.qr_code)}`, { replace: true });
          return;
        }
        if (result.status === "empty_box") {
          const params = new URLSearchParams({ qr: result.qr_code });
          if (result.box_number) params.set("box", String(result.box_number));
          if (result.last_item?.name) params.set("last", result.last_item.name);
          if (result.last_item?.status) params.set("last_status", result.last_item.status);
          navigate(`/add?${params.toString()}`, { replace: true });
          return;
        }
        if (result.status === "locked") {
          navigate(`/locked/${encodeURIComponent(result.qr_code)}`, {
            replace: true,
            state: {
              owner_name: result.owner_name,
              household_name: result.household_name,
            },
          });
          return;
        }
        navigate(`/container/${result.container.id}`, { replace: true });
      } catch (err) {
        if (!cancelled) {
          toast.error(formatError(err));
          navigate("/", { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="px-5 pt-20 pb-10 text-center" data-testid="scan-landing">
      <Loader2 className="w-7 h-7 mx-auto text-[#277A82] animate-spin" />
      <div className="font-heading text-lg font-semibold text-[#2D3142] mt-4">
        Looking up your Q-Box…
      </div>
      <div className="text-xs text-[#8A93A6] mt-1 font-mono">{code}</div>
    </div>
  );
};

export default ScanLanding;
